import React, { use, useMemo } from 'react';
import { useQuery } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { usePersistentPanelSelection } from './hooks/use-persistent-panel-selection';
import styles from './styles.module.scss';
import SubjectProfileTimeline from './subject-profile-timeline';
import TimelinePanelReorderDialog from './timeline-panel-reorder-dialog';
import Button from '@components-clean/core/button';
import { InfoField } from '@components-clean/core/info-field';
import Loader from '@components-clean/core/loader';
import { PanelType } from 'types/graphql-global-types';
import { UserContext } from '@contexts/user-context';
import { useBoolean, useLocalState, useTranslation } from '@hooks/index';
import { GET_SM_PANELS, GET_SUBJECT_PROFILE_DETAILS_CONFIGURATION } from '@queries/subject-monitoring';
import { SubjectMonitoringAPI } from '@apis/bff-api/subject-monitoring/subject-profile';
import './styles.scss';

interface Props {
    orgRef: string;
    studyRef: string;
    datasetId: number;
    datasetCutDate: Date;
    subjectId: number;
}

const SubjectProfileTimelineTab = ({ orgRef, studyRef, datasetId, datasetCutDate, subjectId }: Props) => {
    const { t } = useTranslation('admin');
    const { t: tSm } = useTranslation('sm', {
        keyPrefix: 'subjectProfile',
    });

    const { user } = use(UserContext);
    const { state } = useLocalState();

    const getSubjectDetailsQuery = SubjectMonitoringAPI.useGetSubjectDetails(studyRef, datasetId, subjectId);

    const getSubjectProfileDetailsConfigQueryLive = useQuery(GET_SUBJECT_PROFILE_DETAILS_CONFIGURATION, {
        variables: { studyId: studyRef },
        fetchPolicy: 'no-cache',
    });

    const getSubjectProfileDetailsConfigQueryDataset = useQuery(GET_SUBJECT_PROFILE_DETAILS_CONFIGURATION, {
        variables: { studyId: studyRef, timestamp: new Date(state!.selectedCM2Dataset!.created!) },
        fetchPolicy: 'no-cache',
    });

    const getSmPanelsQuery = useQuery(GET_SM_PANELS, {
        variables: {
            organisationId: user?.organisationId as string,
            studyId: studyRef,
            timestamp: new Date(state!.selectedCM2Dataset!.created!),
        },
    });

    const subjectDetails = getSubjectDetailsQuery.data;
    const subjectDetailsConfigLive =
        getSubjectProfileDetailsConfigQueryLive.data?.subjectProfileDetailsConfiguration
            ?.subjectProfileDetailsConfigurationItem;
    const subjectDetailsConfigDataset =
        getSubjectProfileDetailsConfigQueryDataset.data?.subjectProfileDetailsConfiguration
            ?.subjectProfileDetailsConfigurationItem;

    const showPanelReorderDialog = useBoolean(false);

    // Panels to show set by user on reorder dialog (or null, if dialog never used)
    // Value is persisted in localStorage, if `orgRef`/`studyRef`/`datasetId` is the same as before
    const [selectedPanelIds, setSelectedPanelIds] = usePersistentPanelSelection({
        orgRef,
        studyRef,
        datasetId,
    });

    // Default panels to show configured in config
    const defaultSelectedPanelIds = useMemo(() => {
        return subjectDetailsConfigDataset?.timeline?.panels?.map((panel) => panel.id as string) || [];
    }, [subjectDetailsConfigDataset?.timeline?.panels]);

    // If set, use the panel ids set by the user on the reorder dialog. Otherwise, use the default from the config.
    const resolvedSelectedPanelIds = selectedPanelIds ?? defaultSelectedPanelIds;

    const getTimelinePanelsDataQuery = SubjectMonitoringAPI.useGetTimelinePanelsDataForSubject(
        studyRef,
        datasetId,
        subjectId,
        resolvedSelectedPanelIds,
    );

    const isLoading =
        !user ||
        getSubjectDetailsQuery.isLoading ||
        getSubjectProfileDetailsConfigQueryLive.loading ||
        getSubjectProfileDetailsConfigQueryDataset.loading ||
        getSmPanelsQuery.loading ||
        getTimelinePanelsDataQuery.isLoading;
    const isError =
        getSubjectDetailsQuery.error ||
        getSubjectProfileDetailsConfigQueryLive.error ||
        getSubjectProfileDetailsConfigQueryDataset.error ||
        getSmPanelsQuery.error ||
        getTimelinePanelsDataQuery.error;

    const configuredTimelinePanels = useMemo(() => {
        if (getSmPanelsQuery.data?.smPanels == null) {
            return [];
        }

        const panelsMap = new Map(
            getSmPanelsQuery.data.smPanels
                // Get defined timeline which are active
                .filter((p) => p != null)
                .filter((p) => p.panelType === PanelType.TIMELINE)
                .filter((p) => p.active)
                .map((p) => [p.id as string, p]),
        );

        // Map panelsToShow list to ordered list of panel definitions
        return resolvedSelectedPanelIds.map((id) => panelsMap.get(id)).filter((p) => p != null);
    }, [getSmPanelsQuery.data, resolvedSelectedPanelIds]);

    const reorderDialogAndTrigger = (
        <>
            <Button
                data-testid="timeline-reorder-button"
                className={styles.reorderIcon}
                onClick={showPanelReorderDialog.setTrue}
            >
                <FontAwesomeIcon icon={['far', 'arrow-down-up-across-line']} />
            </Button>
            {showPanelReorderDialog.value && (
                <TimelinePanelReorderDialog
                    smPanelsResponse={getSmPanelsQuery.data?.smPanels ?? []}
                    defaultSelectedPanelIds={defaultSelectedPanelIds}
                    selectedPanelIds={resolvedSelectedPanelIds}
                    onCancel={showPanelReorderDialog.setFalse}
                    onSubmit={(values) => {
                        setSelectedPanelIds(values.selected.map((x) => x.key));
                        showPanelReorderDialog.setFalse();
                    }}
                />
            )}
        </>
    );
    let content;

    if (isError) {
        content = (
            <div className="loading-and-message-container">
                <InfoField type="Error" message={t('common:somethingWentWrong')} />
            </div>
        );
    } else if (isLoading) {
        content = (
            <div className="loading-and-message-container">
                <Loader />
            </div>
        );
    }
    // subjectDetails should always exist
    // subjectDetailsConfig should create, save, and return a default config if one doesn't already exist for the study
    else if (subjectDetails == null || subjectDetailsConfigLive == null) {
        content = (
            <div className="loading-and-message-container">
                <InfoField type="Error" message={t('common:somethingWentWrong')} />
            </div>
        );
    } else if (configuredTimelinePanels.length === 0) {
        content = (
            <>
                {reorderDialogAndTrigger}
                <div className="loading-and-message-container">
                    <InfoField type="Info" message={tSm('timeline.noTimelinePanelsConfigured')} />
                </div>
            </>
        );
    } else if (getTimelinePanelsDataQuery.data == null) {
        content = (
            <div className="loading-and-message-container">
                <InfoField type="Error" message={tSm('timeline.noTimelineDataAvailable')} />
            </div>
        );
    } else {
        content = (
            <>
                {reorderDialogAndTrigger}

                <SubjectProfileTimeline
                    key={subjectId}
                    config={configuredTimelinePanels}
                    data={getTimelinePanelsDataQuery.data}
                    datasetCutDate={datasetCutDate}
                    datasetId={datasetId}
                    subjectId={subjectId}
                    subjectDetails={subjectDetails}
                    subjectDetailsConfig={subjectDetailsConfigLive}
                />
            </>
        );
    }

    return <div className="subject-profile-timeline-tab">{content}</div>;
};

export default SubjectProfileTimelineTab;
